import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {TeamMember} from '../../domain/entities/Pipeline.ts'
import {
    CONVERSION_GROUPS,
    ConversionBoardFilters,
    ConversionCard as ConversionCardData,
    ConversionColumn,
    ConversionCta,
    ConversionGroup,
    ConversionStage,
    isFinalConversionStage,
} from '../../domain/entities/ConversionPipeline.ts'
import {useConversionBoard} from '../../hooks/usePipelines.ts'
import {BoardColumn} from './BoardColumn.tsx'
import {BoardFiltersBar} from './BoardFiltersBar.tsx'
import {ConversionCard} from './ConversionCard.tsx'
import {CardMenuItem} from './PipelineParts.tsx'
import {RunConversionCta} from './useConversionCtaRunner.tsx'

type ConversionBoardProps = {
    filters: ConversionBoardFilters
    onFiltersChange: (filters: ConversionBoardFilters) => void
    team: TeamMember[]
    selectedId: string | null
    onOpenCard: (card: ConversionCardData) => void
    onRunCta: RunConversionCta
    isCtaRunning: (id: string, cta: ConversionCta) => boolean
    ctaBusy: boolean
    onRequestOverride: (card: ConversionCardData) => void
    onRequestLost: (card: ConversionCardData) => void
}

const emptyColumn = (stage: ConversionStage, group: ConversionGroup): ConversionColumn => ({
    stage, group, type: 'EVENT', actor: null, final: false, count: 0, cards: [], nextCursor: null,
})

// Conversion board: the columns the server sends, in their collapsible
// groups. Cards are not dragged: stages move on events and on the card's
// server-driven next steps (CTAs); Override stays in the card menu.
export const ConversionBoard: React.FC<ConversionBoardProps> = ({
    filters, onFiltersChange, team, selectedId, onOpenCard, onRunCta, isCtaRunning, ctaBusy, onRequestOverride, onRequestLost,
}) => {
    const {t} = useTranslation('dashboard')
    const {board, isLoadingBoard, isFetchingBoard, boardError, refetchBoard} = useConversionBoard(filters)
    const [collapsed, setCollapsed] = useState<Partial<Record<ConversionStage, boolean>>>({})
    const [collapsedGroups, setCollapsedGroups] = useState<Partial<Record<ConversionGroup, boolean>>>({})

    // Groups and columns as the server sends them; the static order is only
    // the skeleton before the first response.
    const serverColumns = board?.columns ?? null
    const groupIds: ConversionGroup[] = board?.groups?.length
        ? board.groups.map((g) => g.id)
        : CONVERSION_GROUPS.map((g) => g.id)
    for (const column of serverColumns ?? []) if (!groupIds.includes(column.group)) groupIds.push(column.group)
    const groups = groupIds
        .filter((id) => !filters.group || id === filters.group)
        .map((id) => {
            const columns = serverColumns
                ? serverColumns.filter((c) => c.group === id)
                : [...(CONVERSION_GROUPS.find((g) => g.id === id)?.stages ?? [])].map((stage) => emptyColumn(stage, id))
            const count = board?.groups?.find((bg) => bg.id === id)?.count ?? columns.reduce((sum, c) => sum + c.count, 0)
            const name = board?.groups?.find((bg) => bg.id === id)?.name
            return {id, name, columns, count}
        })
        .filter((g) => g.columns.length > 0)
    const total = groups.reduce((sum, g) => sum + g.count, 0)

    const menuItems = (card: ConversionCardData): CardMenuItem[] => isFinalConversionStage(card.crmStage)
        ? [{label: t('pipelines.card.override'), onSelect: () => onRequestOverride(card)}]
        : [
            {label: t('pipelines.card.override'), onSelect: () => onRequestOverride(card)},
            {label: t('pipelines.card.moveToLostShort'), onSelect: () => onRequestLost(card)},
        ]

    const toggleGroup = (id: ConversionGroup) => setCollapsedGroups((c) => ({...c, [id]: !c[id]}))

    return (
        <div className="flex-1 min-h-0 flex flex-col gap-3.5">
            <div className="flex items-center gap-3 flex-wrap flex-none">
                <BoardFiltersBar pipeline="conversion" filters={filters} team={team} onChange={onFiltersChange}/>
                <span className="flex-1"/>
                <span className="text-[12.5px] text-neutralBlack-600" aria-live="polite">
                    {isFetchingBoard && !isLoadingBoard ? t('pipelines.board.refreshing') : t('pipelines.board.total', {count: total})}
                </span>
            </div>

            <div className="flex-1 min-h-0 flex bg-white border border-neutralBlack-200 rounded-2xl overflow-hidden">
                {boardError && !board ? (
                    <div role="alert" className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
                        <span className="text-[15px] font-semibold text-neutralBlack-900">{t('pipelines.board.errorTitle')}</span>
                        <p className="text-[13px] text-neutralBlack-600 max-w-sm">{t('pipelines.board.errorMessage')}</p>
                        <button type="button" onClick={() => refetchBoard()}
                                className="h-9 px-4 rounded-full bg-neutralBlack-900 text-white text-[13px] font-medium hover:bg-neutralBlack-700 transition-colors duration-200 ease-out">
                            {t('pipelines.retry')}
                        </button>
                    </div>
                ) : (
                    <div className="flex-1 min-w-0 flex gap-5 p-3.5 overflow-x-auto overflow-y-hidden items-stretch" aria-busy={isLoadingBoard}>
                        {groups.map((group) => {
                            const label = t(`pipelines.group.${group.id}`, {defaultValue: group.name ?? group.id})
                            const isCollapsed = !!collapsedGroups[group.id]
                            return (
                                <section key={group.id} aria-label={t('pipelines.group.label', {name: label, count: group.count})}
                                         className="flex-none flex flex-col min-h-0 gap-2">
                                    <div className="flex items-center gap-2 h-6 flex-none">
                                        <button type="button" onClick={() => toggleGroup(group.id)} aria-expanded={!isCollapsed}
                                                aria-label={t(isCollapsed ? 'pipelines.group.expand' : 'pipelines.group.collapse', {name: label})}
                                                className="w-6 h-6 rounded-md flex items-center justify-center text-neutralBlack-600 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                                            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                {isCollapsed ? <path d="m9 6 6 6-6 6"/> : <path d="m6 9 6 6 6-6"/>}
                                            </svg>
                                        </button>
                                        <span className="text-[11px] font-semibold tracking-[.08em] uppercase text-neutralBlack-600 whitespace-nowrap">{label}</span>
                                        <span className="text-[11px] font-medium text-neutralBlack-600">{group.count}</span>
                                        {!isCollapsed && <span aria-hidden="true" className="flex-1 h-px bg-neutralBlack-100 min-w-3"/>}
                                    </div>
                                    <div className="flex-1 min-h-0 flex gap-3">
                                        {isCollapsed ? (
                                            <button type="button" onClick={() => toggleGroup(group.id)} aria-label={t('pipelines.group.expand', {name: label})}
                                                    className="w-[52px] flex-none rounded-[14px] border border-neutralBlack-200 bg-neutralBlack-50 flex flex-col items-center gap-3.5 py-3.5 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                                                <span className="min-w-[22px] h-5 px-1.5 rounded-full bg-neutralBlack-200 text-neutralBlack-700 text-[11.5px] font-semibold inline-flex items-center justify-center">{group.count}</span>
                                                <span className="[writing-mode:vertical-rl] text-[12.5px] font-semibold whitespace-nowrap text-neutralBlack-700">{label}</span>
                                            </button>
                                        ) : isLoadingBoard ? (
                                            group.columns.map((column) => (
                                                <div key={column.stage} className="flex-none w-[300px] rounded-[14px] border border-neutralBlack-200 bg-neutralBlack-50 p-3 flex flex-col gap-2.5">
                                                    <div className="h-5 w-32 rounded bg-neutralBlack-200 animate-pulse"/>
                                                    {Array.from({length: 2}).map((_, i) => <div key={i} className="h-[150px] rounded-xl bg-white animate-pulse"/>)}
                                                </div>
                                            ))
                                        ) : group.columns.map((column) => (
                                            <BoardColumn<ConversionCardData> key={column.stage} column={column}
                                                         source={{pipeline: 'conversion', stage: column.stage, filters}}
                                                         success={column.stage === 'DELIVERED'}
                                                         collapsed={!!collapsed[column.stage]}
                                                         onToggleCollapsed={() => setCollapsed((c) => ({...c, [column.stage]: !c[column.stage]}))}
                                                         renderCard={(card) => (
                                                             <ConversionCard card={card} team={team} menuItems={menuItems(card)}
                                                                             selected={card.id === selectedId} onOpen={onOpenCard}
                                                                             onRunCta={onRunCta} isCtaRunning={isCtaRunning} ctaBusy={ctaBusy}/>
                                                         )}/>
                                        ))}
                                    </div>
                                </section>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}
